import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { profileService } from "../lib/profileService";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Building2,
  Code,
  Megaphone,
  LogOut,
  User,
  Phone,
  Settings,
} from "lucide-react";

export function Dashboard() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProfile();
  }, [user]);

  const loadProfile = async () => {
    if (!user) return;

    try {
      const data = await profileService.getProfile();
      setProfile(data);
    } catch (error) {
      console.error("Error loading profile:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate("/");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const handleRequestService = (serviceName) => {
    const message = `Olá! Gostaria de solicitar o serviço: ${serviceName}`;
    const whatsappUrl = `https://wa.me/5567991768713?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-vitapink-100 via-white to-gray-50 flex items-center justify-center">
        <p className="text-lg text-gray-600">Carregando...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-vitapink-100 via-white to-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">VitaCode</h1>
            <p className="text-sm text-gray-500">
              Bem-vindo, {profile?.username || user?.email}
            </p>
          </div>
          <div className="flex gap-2 items-center">
            {user?.role === "admin" && (
              <Button
                variant="outline"
                onClick={() => navigate("/admin")}
                className="border-vitapink text-vitapink hover:bg-vitapink-50"
              >
                <Settings className="w-4 h-4 mr-2" />
                Painel Admin
              </Button>
            )}
            <Button variant="outline" onClick={handleSignOut}>
              <LogOut className="w-4 h-4 mr-2" />
              Sair
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Profile Info */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Seu Perfil
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-gray-500">Nome</p>
              <p className="font-semibold">{profile?.username}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Empresa</p>
              <p className="font-semibold">{profile?.company_name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Contato</p>
              <p className="font-semibold">{profile?.contact_number}</p>
            </div>
          </CardContent>
        </Card>

        {/* Systems Section */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Nossos Sistemas
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* VitaFlow */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-vitapink" />
                    VitaFlow
                  </CardTitle>
                  <Badge className="bg-vitapink text-white">
                    Lançamento 01/03/2026
                  </Badge>
                </div>
                <CardDescription>Sistema de Gestão Financeira</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-4">
                  Controle completo das suas finanças com sincronização bancária
                  via Open Finance, simulação de reforma tributária e modo
                  offline.
                </p>
                <Button
                  className="w-full bg-vitapink hover:bg-vitapink-700"
                  onClick={() =>
                    handleRequestService("VitaFlow - Gestão Financeira")
                  }
                >
                  Solicitar Acesso
                </Button>
              </CardContent>
            </Card>

            {/* CRM Escolar */}
            <Card className="hover:shadow-lg transition-shadow opacity-75">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-gray-400" />
                    CRM Escolar
                  </CardTitle>
                  <Badge variant="outline">Em Breve</Badge>
                </div>
                <CardDescription>Sistema de Gestão Escolar</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-4">
                  Gerencie sua instituição de ensino com controle de alunos,
                  professores, matrículas e muito mais.
                </p>
                <Button className="w-full" variant="outline" disabled>
                  Em Desenvolvimento
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Services Section */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Nossos Serviços
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Desenvolvimento */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Code className="w-5 h-5 text-vitapink" />
                  Desenvolvimento
                </CardTitle>
                <CardDescription>
                  Sites e Aplicativos Personalizados
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="text-sm text-gray-600 mb-4 space-y-2">
                  <li>• E-commerce profissional</li>
                  <li>• Sistemas de gestão customizados</li>
                  <li>• Aplicativos mobile</li>
                </ul>
                <Button
                  className="w-full bg-vitapink hover:bg-vitapink-700"
                  onClick={() =>
                    handleRequestService("Desenvolvimento de Site/App")
                  }
                >
                  Solicitar Orçamento
                </Button>
              </CardContent>
            </Card>

            {/* Gestão de Mídias */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Megaphone className="w-5 h-5 text-vitapink" />
                  Gestão de Mídias
                </CardTitle>
                <CardDescription>Marketing Digital e Conteúdo</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="text-sm text-gray-600 mb-4 space-y-2">
                  <li>• Edição de vídeos profissionais</li>
                  <li>• Marketing visual</li>
                  <li>• Tráfego pago otimizado</li>
                </ul>
                <Button
                  className="w-full bg-vitapink hover:bg-vitapink-700"
                  onClick={() => handleRequestService("Gestão de Mídias")}
                >
                  Solicitar Orçamento
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
